import {useCallback, useEffect} from 'react'
import {useApolloClient, useMutation, useQuery} from '@apollo/react-hooks'
import gql from 'graphql-tag'

import {HeldWithdrawal, VIDEO_VERIFICATION_HOLD_CODE} from './utils'

// The server returns only "required: yes or no", "available: yes or no" and the status. The limit and the total never reach the client.
export const VIDEO_VERIFICATION_QUERY = gql`
  query VideoVerification {
    viewer {
      ... on Client {
        id
        videoVerification {
          required
          available
          status
          hasHeldWithdrawals
        }
        heldVideoVerificationWithdrawals: withdrawals(
          holdCode: "${VIDEO_VERIFICATION_HOLD_CODE}"
          statuses: [onHold]
          limit: 50
        ) {
          id
          amount
          currency
          paymentVendor
          account {
            __typename
            ... on BaseAccount {
              id
              remoteId
              accountName
              connectedBrokereeProviderAccount {
                id
                remoteId
              }
            }
          }
        }
      }
    }
  }
`

const START_VIDEO_VERIFICATION_MUTATION = gql`
  mutation StartVideoVerification {
    startVideoVerification {
      required
      status
      hasHeldWithdrawals
    }
  }
`

const VIDEO_VERIFICATION_SDK_TOKEN_QUERY = gql`
  query VideoVerificationSdkToken {
    videoVerificationSdkToken
  }
`

const POLL_INTERVAL_MS = 30000

export type VideoVerificationData = {
  loading: boolean
  required: boolean
  /** PT-7834: the check applies to this client now. Banner I shows only then. */
  available: boolean
  status: string | null
  hasHeldWithdrawals: boolean
  heldWithdrawals: HeldWithdrawal[]
  refetch: () => Promise<unknown>
}

/** The video verification state of the signed-in client and the withdrawals that wait for it. */
export function useVideoVerification(skip = false): VideoVerificationData {
  // errorPolicy none: a failed field answers `viewer: null`, and Apollo would write that into the shared cache.
  const {data, loading, refetch, startPolling, stopPolling} = useQuery(VIDEO_VERIFICATION_QUERY, {
    skip,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'none',
  })
  const viewer = data?.viewer
  const state = viewer?.videoVerification
  const hasHeldWithdrawals = Boolean(state?.hasHeldWithdrawals)

  // Sumsub answers by webhook, so a waiting client sees the result without a reload.
  useEffect(() => {
    if (skip || !hasHeldWithdrawals) return undefined
    startPolling(POLL_INTERVAL_MS)
    const handleFocus = () => {
      refetch().catch(() => undefined)
    }
    window.addEventListener('focus', handleFocus)
    return () => {
      stopPolling()
      window.removeEventListener('focus', handleFocus)
    }
  }, [skip, hasHeldWithdrawals, startPolling, stopPolling, refetch])

  return {
    loading,
    required: Boolean(state?.required),
    available: Boolean(state?.available),
    status: state?.status ?? null,
    hasHeldWithdrawals,
    heldWithdrawals: viewer?.heldVideoVerificationWithdrawals ?? [],
    refetch,
  }
}

/** Marks the start of the video verification and gets a fresh Sumsub SDK token. A token is never cached. */
export function useSumsubSession() {
  const client = useApolloClient()
  const [start] = useMutation(START_VIDEO_VERIFICATION_MUTATION)

  const fetchToken = useCallback(async (): Promise<string> => {
    const {data} = await client.query({query: VIDEO_VERIFICATION_SDK_TOKEN_QUERY, fetchPolicy: 'no-cache'})
    const token = data?.videoVerificationSdkToken
    if (!token) throw new Error('The server returned no Sumsub token')
    return token
  }, [client])

  const startSession = useCallback(async (): Promise<string> => {
    await start()
    return fetchToken()
  }, [start, fetchToken])

  return {startSession, fetchToken}
}
